/**
 * A nominal's decimal places carry its tolerance (1.000 is tighter than 1), but the
 * server stores every nominal with five places. The places written are kept in
 * `nominal_decimals` and used to show the value back as it was entered.
 */

export const MAX_NOMINAL_DECIMALS = 5;

/** What a nominal field accepts while typing: digits, one point, a leading minus. */
export const NOMINAL_INPUT_PATTERN = /^-?\d*\.?\d{0,5}$/;

const isBlank = (value: unknown) => value === null || value === undefined || value === '';

/** A nominal the server can store; false for blanks and half-typed text like "-". */
export const isNominalNumber = (value: string | number | null | undefined): boolean =>
	!isBlank(value) && /\d/.test(String(value)) && Number.isFinite(Number(value));

/** Places written in `text` ("1.000" → 3, "1" → 0), or null when there is no number. */
export const nominalDecimals = (text: string | number | null | undefined): number | null => {
	if (isBlank(text)) return null;
	const s = String(text).trim();
	if (!/\d/.test(s)) return null;
	const point = s.indexOf('.');
	const places = point < 0 ? 0 : s.length - point - 1;
	return Math.min(places, MAX_NOMINAL_DECIMALS);
};

/** The nominal as written: padded or trimmed to `decimals`, or as-is when unknown. */
export const formatNominal = (
	nominal: string | number | null | undefined,
	decimals: number | null | undefined
): string => {
	if (isBlank(nominal)) return '';
	const s = String(nominal);
	const n = Number(s);
	if (!Number.isFinite(n)) return s;
	if (decimals === null || decimals === undefined) return String(n);
	// Text being typed ("1.", "1.0") already has the places it claims; the server's
	// "1.00000" does not, so it is re-rendered.
	return nominalDecimals(s) === decimals ? s : n.toFixed(decimals);
};
